import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, MetricTile, Card, CardHeader, CBNBadge, AtomBadge, RiskPill, StatusPill, PriorityPill } from "../components/primitives";
import { AlertTicker } from "../components/AlertTicker";
import { NigeriaMap } from "../components/NigeriaMap";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend, PieChart, Pie, Cell, Area, AreaChart, RadialBarChart, RadialBar } from "recharts";
import { Link } from "react-router-dom";
import { formatNGN, formatRelative } from "../lib/format";
import { TrendingUp, AlertTriangle, ShieldCheck, FileText, Activity, Users, Banknote, Globe2, Clock, Sparkles, X } from "lucide-react";

const NIGERIAN_STATE_COLORS = {};

const TACTIC_COLORS = {
  Placement: "#0B2B5C",
  Layering: "#1E3A8A",
  Integration: "#3F5F8D",
  "Illicit Acquisition": "#B7791F",
  Concealment: "#C2410C",
  "Operational Evasion": "#991B1B",
  "Access Facilitation": "#F59A2D",
  "Asset Protection": "#5C5C57",
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [cbn, setCbn] = useState([]);
  const [models, setModels] = useState([]);
  const [audit, setAudit] = useState([]);
  const [cases, setCases] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [stateDrill, setStateDrill] = useState(null);
  const [mapMode, setMapMode] = useState("registration");
  const [stateAlerts, setStateAlerts] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);

  useEffect(() => {
    if (!stateDrill) return;
    setStateLoading(true);
    api.get(`/dashboard/state-drill?state=${encodeURIComponent(stateDrill)}`)
      .then(r => setStateAlerts(r.data))
      .finally(() => setStateLoading(false));
  }, [stateDrill]);

  const runExecSummary = async () => {
    setAiOpen(true);
    setAiLoading(true);
    setAiText("");
    try {
      const r = await api.post("/atom/executive-summary", {}, { timeout: 90000 });
      setAiText(r.data.narrative || "No narrative returned.");
    } catch (e) {
      setAiText("Atom Nucleus is unavailable at the moment. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    api.get("/dashboard/summary").then(r => setSummary(r.data));
    api.get("/alerts").then(r => setAlerts(r.data.alerts || []));
    api.get("/cbn/sections").then(r => setCbn(r.data.sections || []));
    api.get("/models").then(r => setModels(r.data.models || []));
    api.get("/audit?limit=10").then(r => setAudit(r.data.entries || []));
    api.get("/cases").then(r => setCases(r.data.cases || []));
  }, []);

  if (!summary) return <div className="p-6 text-[13px] text-ink-muted">Loading compliance dashboard…</div>;

  const cases5 = cases.slice(0, 5);

  return (
    <div>
      <PageHeader
        title="Compliance Dashboard"
        subtitle="Morning workspace for the Compliance Group · 90-day operating window"
        sections={["5.4","5.5","5.6","5.9","AI/ML"]}
        atom={<div className="flex gap-1.5 items-center">
          <AtomBadge model="Atom Proton" version="v2.4" description="Risk scoring engine" />
          <AtomBadge model="Atom Nucleus" version="v3.5" description="Narrative drafting" />
          <button data-testid="exec-summary-btn" onClick={runExecSummary} className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] bg-ink text-white rounded-full hover:bg-navy transition-colors">
            <Sparkles size={11} strokeWidth={1.5} /> Generate executive summary
          </button>
        </div>}
      />

      <div className="p-6 space-y-6">
        {/* Live alert ticker */}
        <AlertTicker alerts={alerts} />

        {/* Metric tiles */}
        <div className="grid grid-cols-6 gap-3">
          <MetricTile testid="tile-open-alerts" label="Open alerts" value={summary.open_alerts} sub={`P1 ${summary.open_alerts_p1} · P2 ${summary.open_alerts_p2} · P3 ${summary.open_alerts_p3} · P4 ${summary.open_alerts_p4}`} trend={-8} />
          <MetricTile testid="tile-high-risk" label="High-risk customers" value={summary.high_risk_customers} sub={`+${summary.high_risk_new_week} elevated this week`} />
          <MetricTile testid="tile-sanctions" label="Sanctions hits · 7d" value={summary.sanctions_hits_week} sub={`${summary.sanctions_confirmed} confirmed · ${summary.sanctions_false_positive} false positives`} />
          <MetricTile testid="tile-strs" label="STRs filed · month" value={summary.strs_filed_month} sub={`${summary.strs_acked} acknowledged · ${summary.strs_pending} pending NFIU`} trend={12} />
          <MetricTile testid="tile-model-health" label="Atom model health" value={summary.model_health} sub={`${summary.models_green} green · ${summary.models_amber} amber · ${summary.models_red} red`} />
          <MetricTile testid="tile-readiness" label="Examination readiness" value={summary.examination_readiness} sub={`${summary.examination_days_to_next} days to next internal review`} trend={3} />
        </div>

        {/* Row 2: Alert volume + Geo */}
        <div className="grid grid-cols-2 gap-3">
          <Card testid="card-tactic-volume">
            <CardHeader title="Alert volume by AMLTRIX tactic — last 14 days" subtitle="Stacked daily counts; click bars to drill down" />
            <div className="p-3 h-[320px]">
              <ResponsiveContainer>
                <BarChart data={summary.alerts_by_tactic_14d} margin={{top:5,right:8,left:-22,bottom:0}} barCategoryGap={3}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#EEEBE3" vertical={false} />
                  <XAxis dataKey="day" tick={{fontSize:9,fill:'#8A877E'}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fontSize:9,fill:'#8A877E'}} axisLine={false} tickLine={false} width={28} />
                  <Tooltip contentStyle={{fontSize:11,border:'1px solid #E5E5E0',borderRadius:4,padding:'4px 8px'}} />
                  {Object.entries(TACTIC_COLORS).map(([k,c]) => (
                    <Bar key={k} dataKey={k} stackId="a" fill={c} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card testid="card-geo">
            <CardHeader title="Geographic distribution" subtitle={mapMode === "registration" ? "Click a state bubble to drill into alerts" : "Where transactions are originating from · IP-based"} right={
              <div className="inline-flex border border-line rounded-md p-0.5 bg-bg-warm/40 text-[10px]">
                <button data-testid="map-mode-reg" onClick={() => setMapMode("registration")} className={`px-2 py-0.5 rounded transition-colors ${mapMode === "registration" ? "bg-bg-surface text-ink font-medium shadow-sm" : "text-ink-muted hover:text-ink"}`}>Customer registration</button>
                <button data-testid="map-mode-ip" onClick={() => setMapMode("ip")} className={`px-2 py-0.5 rounded transition-colors ${mapMode === "ip" ? "bg-bg-surface text-ink font-medium shadow-sm" : "text-ink-muted hover:text-ink"}`}>Transaction IP</button>
              </div>
            } />
            <div className="p-4">
              {mapMode === "ip" && (
                <div className="grid grid-cols-3 gap-2 mb-3 text-[11px]">
                  <div className="border border-line rounded-md p-2 bg-bg-warm/30"><div className="text-ink-subtle text-[10px] uppercase tracking-wide">Tx today</div><div className="display text-[16px] font-semibold text-ink tabular">14.2M</div></div>
                  <div className="border border-line rounded-md p-2 bg-bg-warm/30"><div className="text-ink-subtle text-[10px] uppercase tracking-wide">Foreign IP</div><div className="display text-[16px] font-semibold text-ink tabular">47,892 <span className="text-[10px] text-ink-subtle">(0.34%)</span></div></div>
                  <div className="border border-amber/30 rounded-md p-2 bg-amber/8"><div className="text-amber-700 text-[10px] uppercase tracking-wide">Anomalous IPs</div><div className="display text-[16px] font-semibold text-amber-700 tabular">156 customers</div></div>
                </div>
              )}
              <NigeriaMap data={summary.alerts_by_state} height={340} onStateClick={(s) => setStateDrill(s)} />
              {mapMode === "registration" ? (
                <div className="mt-3 text-[11px] text-ink-subtle">Lagos, FCT, Rivers, and Kano show the highest concentration, consistent with branch density and high-value customer distribution.</div>
              ) : (
                <div className="mt-3 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wide text-ink-subtle font-medium">Anomalies flagged · Atom Proton v2.4</div>
                  {[
                    { tone: "amber", text: "12 customers registered in Lagos transacting from VPN endpoints in Hong Kong in last 48h" },
                    { tone: "amber", text: "8 customers registered in Abuja with sudden velocity from Lagos IPs (geographic profile shift)" },
                    { tone: "red", text: "3 customers transacting from sanctioned-jurisdiction IPs (Iran) — auto-frozen pending review" },
                  ].map((a, i) => (
                    <button key={i} onClick={() => alert(a.text)} className={`w-full text-left text-[11px] px-2 py-1.5 rounded border ${a.tone === "red" ? "bg-risk-veryhigh/8 border-risk-veryhigh/30 text-risk-veryhigh" : "bg-amber/8 border-amber/30 text-amber-700"} hover:opacity-80 transition-opacity`}>{a.text}</button>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Row 3: cases + CBN + models */}
        <div className="grid grid-cols-3 gap-3">
          <Card testid="card-top-cases">
            <CardHeader title="Latest high-priority cases" right={<Link to="/cases" className="text-[11px] text-amber-600 hover:underline">View all →</Link>} />
            <div>
              {cases5.map((c) => (
                <Link key={c.id} to={`/cases/${c.id}`} className="flex items-start gap-2 p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                  <PriorityPill priority={c.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{c.subject}</div>
                    <div className="text-[11px] text-ink-subtle mt-0.5 truncate">{c.id} · {c.status}</div>
                    <div className="text-[11px] text-ink-muted mt-0.5">{c.owner}</div>
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          <Card testid="card-cbn-readiness">
            <CardHeader title="CBN directive readiness" subtitle="Twelve capability areas + AI/ML governance" />
            <div className="p-3 grid grid-cols-2 gap-1.5">
              {cbn.map((s) => (
                <div key={s.code} className="border border-line rounded-sm p-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium">{s.code}</span>
                    <span className={`w-1.5 h-1.5 rounded-full ${s.status === 'green' ? 'bg-risk-low' : s.status === 'amber' ? 'bg-risk-medium' : 'bg-risk-high'}`} />
                  </div>
                  <div className="text-[10px] text-ink-subtle truncate">{s.name}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card testid="card-model-health">
            <CardHeader title="Atom AI · model performance" />
            <div>
              {models.map((m) => (
                <Link key={m.id} to={`/models/${m.id}`} className="flex items-center justify-between p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium">{m.name} <span className="text-[11px] text-ink-subtle">{m.version}</span></div>
                    <div className="text-[10px] text-ink-subtle">{m.precision ? `P ${m.precision.toFixed(2)} · R ${m.recall.toFixed(2)}` : '—'} · {m.last_validation}</div>
                  </div>
                  <span className={`w-1.5 h-1.5 rounded-full ${m.drift === 'Amber' ? 'bg-risk-medium' : 'bg-risk-low'}`} />
                </Link>
              ))}
            </div>
          </Card>
        </div>

        {/* Row 4: audit / approvals / culture */}
        <div className="grid grid-cols-3 gap-3">
          <Card testid="card-audit">
            <CardHeader title="Recent audit activity" right={<Link to="/governance/audit-log" className="text-[11px] text-amber-600 hover:underline">Full log →</Link>} />
            <div>
              {audit.slice(0, 8).map((a) => (
                <div key={a.id} className="px-3 py-2 border-b border-line/50 last:border-0 text-[11px]">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium truncate">{a.user}</span>
                    <span className="text-ink-subtle text-[10px] shrink-0">{formatRelative(a.ts)}</span>
                  </div>
                  <div className="text-ink-subtle truncate">{a.action} · {a.entity_id}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card testid="card-approvals">
            <CardHeader title="Maker-checker queue" subtitle="Actions awaiting your approval" />
            <div className="p-3 space-y-2">
              {[
                ["Threshold change","RULE-STR-001 → ₦4.5M","Marcus Whitfield"],
                ["Case closure (cleared)","CSE-2026-00115","Noah Kessler"],
                ["STR submission","STR-2026-045","Ethan Brennan"],
                ["Suppression rule","Counterparty: Glo Nigeria","Daniel Castellanos"],
              ].map(([t, ctx, m], i) => (
                <div key={i} className="border border-line rounded-md p-2">
                  <div className="text-[12px] font-medium">{t}</div>
                  <div className="text-[11px] text-ink-subtle">{ctx} · proposed by {m}</div>
                  <div className="flex gap-1 mt-1.5">
                    <button className="px-2 py-0.5 text-[10px] bg-navy text-white rounded-sm hover:bg-navy-700">Review</button>
                    <button className="px-2 py-0.5 text-[10px] border border-line rounded-sm hover:bg-bg-hover">Defer</button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card testid="card-culture">
            <CardHeader title="Compliance culture indicators" subtitle="KPMG seven-blocks framework · firm-wide scores" right={<span className="text-[10px] text-ink-subtle">Updated 22 May 2026</span>} />
            <div className="p-3 space-y-2.5 text-[11px]">
              {/* Block 1 — Leadership tone & vision */}
              <div>
                <div className="flex justify-between mb-1"><span className="font-medium">1 · Leadership tone & vision</span><span className="font-medium tabular text-risk-low">88%</span></div>
                <div className="h-1.5 bg-bg-subtle rounded-sm"><div className="h-full bg-risk-low rounded-sm" style={{width:"88%"}} /></div>
                <div className="text-ink-subtle mt-0.5">CEO ratified the 2026 compliance charter on 14 Apr; board AML champion designated.</div>
              </div>
              {/* Block 2 — Risk appetite & policies */}
              <div>
                <div className="flex justify-between mb-1"><span className="font-medium">2 · Risk appetite & policies</span><span className="font-medium tabular text-risk-medium">62%</span></div>
                <div className="h-1.5 bg-bg-subtle rounded-sm"><div className="h-full bg-risk-medium rounded-sm" style={{width:"62%"}} /></div>
                <div className="text-ink-subtle mt-0.5">Within tolerance; trending towards upper band. Q3 review scheduled with the Board Risk Committee.</div>
              </div>
              {/* Block 3 — Governance & accountability */}
              <div>
                <div className="flex justify-between mb-1"><span className="font-medium">3 · Governance & accountability</span><span className="font-medium tabular text-risk-low">81%</span></div>
                <div className="h-1.5 bg-bg-subtle rounded-sm"><div className="h-full bg-risk-low rounded-sm" style={{width:"81%"}} /></div>
                <div className="text-ink-subtle mt-0.5">MLRO independence affirmed; quarterly compliance committee attendance 100%.</div>
              </div>
              {/* Block 4 — Training & competence */}
              <div>
                <div className="flex justify-between mb-1"><span className="font-medium">4 · Training & competence (Q2)</span><span className="font-medium tabular text-risk-low">94%</span></div>
                <div className="h-1.5 bg-bg-subtle rounded-sm"><div className="h-full bg-risk-low rounded-sm" style={{width:"94%"}} /></div>
                <div className="text-ink-subtle mt-0.5">4,532 of 4,820 staff completed CBN-aligned AML modules.</div>
              </div>
              {/* Block 5 — Performance management & incentives */}
              <div>
                <div className="flex justify-between mb-1"><span className="font-medium">5 · Performance mgmt & incentives</span><span className="font-medium tabular text-risk-medium">71%</span></div>
                <div className="h-1.5 bg-bg-subtle rounded-sm"><div className="h-full bg-risk-medium rounded-sm" style={{width:"71%"}} /></div>
                <div className="text-ink-subtle mt-0.5">Compliance KPI weight raised to 15% of variable pay for revenue staff (was 10%).</div>
              </div>
              {/* Block 6 — Reporting & escalation */}
              <div>
                <div className="flex justify-between mb-1"><span className="font-medium">6 · Reporting & escalation</span><span className="font-medium tabular text-risk-low">86%</span></div>
                <div className="h-1.5 bg-bg-subtle rounded-sm"><div className="h-full bg-risk-low rounded-sm" style={{width:"86%"}} /></div>
                <div className="text-ink-subtle mt-0.5">Whistleblower pipeline: 12 internal tips this quarter, 3 escalated to MLRO, all closed within SLA.</div>
              </div>
              {/* Block 7 — Monitoring & assurance */}
              <div>
                <div className="flex justify-between mb-1"><span className="font-medium">7 · Monitoring & assurance</span><span className="font-medium tabular text-risk-low">79%</span></div>
                <div className="h-1.5 bg-bg-subtle rounded-sm"><div className="h-full bg-risk-low rounded-sm" style={{width:"79%"}} /></div>
                <div className="text-ink-subtle mt-0.5">Internal audit's H1 AML thematic review: 7 findings, 6 closed, 1 in remediation.</div>
              </div>

              {/* Board engagement footer */}
              <div className="pt-2 border-t border-line">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-medium">Board engagement</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-risk-low/15 text-risk-low font-medium">Strong</span>
                </div>
                <div>Last AML report tabled <span className="font-medium">22 Apr 2026</span> · next 24 Jul 2026</div>
                <div className="text-ink-subtle">Headline: structuring rule tuning approved; new PEP screening cadence ratified; Atom Proton v2.4 drift remediation on track.</div>
              </div>

              {/* Composite score */}
              <div className="pt-2 border-t border-line flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Composite culture index</div>
                  <div className="display text-[20px] font-semibold tabular">80<span className="text-[12px] text-ink-subtle">/100</span></div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Peer benchmark</div>
                  <div className="text-[12px] font-medium tabular">73 <span className="text-risk-low text-[10px]">+7 vs peers</span></div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Row 5: Channel mix + Risk distribution + STR trend */}
        <div className="grid grid-cols-3 gap-3">
          <Card testid="card-channel-mix">
            <CardHeader title="Channel mix · last 7 days" subtitle="Where transactions originate" />
            <div className="p-3 h-[180px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={[
                    {name:"Mobile app",value:35,fill:"#0F172A"},
                    {name:"USSD",value:15,fill:"#1E293B"},
                    {name:"Internet banking",value:12,fill:"#475569"},
                    {name:"ATM",value:10,fill:"#1F7A4D"},
                    {name:"POS",value:10,fill:"#B7791F"},
                    {name:"Branch",value:8,fill:"#F59A2D"},
                    {name:"NIBSS Instant Payment",value:7,fill:"#C2410C"},
                    {name:"RTGS",value:2,fill:"#991B1B"},
                    {name:"Cheque",value:1,fill:"#56544D"},
                  ]} innerRadius={38} outerRadius={62} paddingAngle={2} dataKey="value" stroke="#FFFFFF" strokeWidth={1.5} />
                  <Tooltip contentStyle={{fontSize:11,border:'1px solid #E8E5DC',borderRadius:8}} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="px-3 pb-3 grid grid-cols-3 gap-1 text-[10px]">
              {[["Mobile","35%","#0B2B5C"],["USSD","15%","#1E3A8A"],["IB","12%","#3F5F8D"],["ATM","10%","#1F7A4D"],["POS","10%","#B7791F"],["Branch","8%","#F59A2D"]].map(([n,v,c]) => (
                <div key={n} className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{background:c}} /><span className="text-ink-muted truncate">{n}</span><span className="tabular ml-auto font-medium">{v}</span></div>
              ))}
            </div>
          </Card>

          <Card testid="card-risk-dist">
            <CardHeader title="Customer risk distribution" subtitle="1,500 customer base" />
            <div className="p-3 h-[180px]">
              <ResponsiveContainer>
                <BarChart data={[
                  {band:"Low",value:900,fill:"#1F7A4D"},
                  {band:"Medium",value:420,fill:"#B7791F"},
                  {band:"High",value:150,fill:"#C2410C"},
                  {band:"Very High",value:30,fill:"#991B1B"},
                ]} layout="vertical" margin={{top:5,right:30,left:0,bottom:5}}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E8E5DC" horizontal={false} />
                  <XAxis type="number" tick={{fontSize:10,fill:'#56544D'}} axisLine={false} tickLine={false} />
                  <YAxis dataKey="band" type="category" tick={{fontSize:11,fill:'#161614'}} axisLine={false} tickLine={false} width={80} />
                  <Tooltip contentStyle={{fontSize:11,border:'1px solid #E8E5DC',borderRadius:8}} />
                  <Bar dataKey="value" radius={[0,6,6,0]}>
                    {[{fill:"#1F7A4D"},{fill:"#B7791F"},{fill:"#C2410C"},{fill:"#991B1B"}].map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card testid="card-str-trend">
            <CardHeader title="STR filings · 12 months" subtitle="Submitted to NFIU" right={<TrendingUp size={14} className="text-risk-low" />} />
            <div className="p-3 h-[180px]">
              <ResponsiveContainer>
                <AreaChart data={[
                  {m:"Jun",v:28},{m:"Jul",v:32},{m:"Aug",v:31},{m:"Sep",v:38},{m:"Oct",v:42},{m:"Nov",v:36},
                  {m:"Dec",v:44},{m:"Jan",v:48},{m:"Feb",v:45},{m:"Mar",v:51},{m:"Apr",v:49},{m:"May",v:47},
                ]} margin={{top:5,right:10,left:-20,bottom:5}}>
                  <defs>
                    <linearGradient id="strGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59A2D" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#F59A2D" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E8E5DC" vertical={false} />
                  <XAxis dataKey="m" tick={{fontSize:10,fill:'#56544D'}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fontSize:10,fill:'#56544D'}} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{fontSize:11,border:'1px solid #E8E5DC',borderRadius:8}} />
                  <Area type="monotone" dataKey="v" stroke="#F59A2D" strokeWidth={2} fill="url(#strGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Row 6: Top counterparties + Time-of-day + AMLTRIX coverage */}
        <div className="grid grid-cols-3 gap-3">
          <Card testid="card-counterparties">
            <CardHeader title="Top counterparty banks · this week" subtitle="By transaction volume" right={<Globe2 size={14} className="text-ink-muted" />} />
            <div className="p-3 space-y-1.5">
              {[
                ["Northwind Bank","₦82.4M",92],
                ["Crystal Commerce Bank","₦68.9M",78],
                ["Meridian Federal Bank","₦54.2M",62],
                ["Helios Bank","₦47.8M",54],
                ["Heritage Trust Bank","₦41.3M",47],
                ["ZipPay","₦38.6M",44],
                ["CloudPoint","₦29.1M",33],
                ["Solstice Bank","₦22.4M",26],
              ].map(([n, v, w]) => (
                <div key={n}>
                  <div className="flex items-center justify-between text-[12px] mb-1">
                    <span className="truncate">{n}</span>
                    <span className="tabular text-ink-muted">{v}</span>
                  </div>
                  <div className="h-1 bg-bg-subtle rounded-full"><div className="h-full bg-navy rounded-full" style={{width: `${w}%`}} /></div>
                </div>
              ))}
            </div>
          </Card>

          <Card testid="card-tod">
            <CardHeader title="Activity by hour · today" subtitle="Transaction volume heatmap" right={<Clock size={14} className="text-ink-muted" />} />
            <div className="p-3 h-[180px]">
              <ResponsiveContainer>
                <BarChart data={Array.from({length:24}, (_, h) => ({
                  hour: `${String(h).padStart(2,'0')}h`,
                  v: h<6 ? Math.random()*40+5 : h<9 ? Math.random()*180+80 : h<12 ? Math.random()*320+220 : h<15 ? Math.random()*220+150 : h<19 ? Math.random()*340+260 : h<22 ? Math.random()*180+90 : Math.random()*60+15,
                }))} margin={{top:5,right:5,left:-25,bottom:5}}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E8E5DC" vertical={false} />
                  <XAxis dataKey="hour" tick={{fontSize:9,fill:'#56544D'}} interval={3} axisLine={false} tickLine={false} />
                  <YAxis tick={{fontSize:10,fill:'#56544D'}} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{fontSize:11,border:'1px solid #E8E5DC',borderRadius:8}} />
                  <Bar dataKey="v" fill="#0B2B5C" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card testid="card-amltrix">
            <CardHeader title="AMLTRIX typology coverage" subtitle="Detected vs total in taxonomy" />
            <div className="p-3 h-[180px]">
              <ResponsiveContainer>
                <RadialBarChart innerRadius="35%" outerRadius="90%" data={[
                  {name:"Placement",value:88,fill:"#0B2B5C"},
                  {name:"Layering",value:74,fill:"#1E3A8A"},
                  {name:"Integration",value:62,fill:"#3F5F8D"},
                  {name:"Concealment",value:91,fill:"#F59A2D"},
                  {name:"Op. Evasion",value:55,fill:"#B7791F"},
                ]} startAngle={90} endAngle={-270}>
                  <RadialBar background dataKey="value" cornerRadius={4} />
                  <Tooltip contentStyle={{fontSize:11,border:'1px solid #E8E5DC',borderRadius:8}} />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
            <div className="px-3 pb-3 grid grid-cols-2 gap-1 text-[10px]">
              {[["Placement","88%","#0B2B5C"],["Layering","74%","#1E3A8A"],["Integration","62%","#3F5F8D"],["Concealment","91%","#F59A2D"],["Op. Evasion","55%","#B7791F"]].map(([n,v,c]) => (
                <div key={n} className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{background:c}} /><span className="text-ink-muted truncate">{n}</span><span className="tabular ml-auto font-medium">{v}</span></div>
              ))}
            </div>
          </Card>
        </div>

        {/* Row 7: Sanctions hits trend + Onboarding funnel + Compliance KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <Card testid="card-sanctions-trend">
            <CardHeader title="Sanctions screening · 30 days" subtitle="Clear vs hits vs false positives" right={<ShieldCheck size={14} className="text-risk-low" />} />
            <div className="p-3 h-[170px]">
              <ResponsiveContainer>
                <LineChart data={Array.from({length:30},(_,i) => ({
                  d: `${i+1} May`,
                  clear: 1200+Math.random()*200,
                  hit: Math.random()*3+(i>=20?2:0),
                  fp: Math.random()*5+2
                }))} margin={{top:5,right:10,left:-15,bottom:5}}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E8E5DC" vertical={false} />
                  <XAxis dataKey="d" tick={{fontSize:9,fill:'#56544D'}} interval={5} axisLine={false} tickLine={false} />
                  <YAxis tick={{fontSize:10,fill:'#56544D'}} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{fontSize:11,border:'1px solid #E8E5DC',borderRadius:8}} />
                  <Line type="monotone" dataKey="hit" stroke="#991B1B" strokeWidth={2} dot={false} name="Hits" />
                  <Line type="monotone" dataKey="fp" stroke="#B7791F" strokeWidth={2} dot={false} name="False positives" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card testid="card-onboarding">
            <CardHeader title="Onboarding funnel · this month" subtitle="From application to active customer" right={<Users size={14} className="text-ink-muted" />} />
            <div className="p-4 space-y-2.5">
              {[
                ["Applications received",1840,100,"#0B2B5C"],
                ["BVN verified",1762,96,"#1E3A8A"],
                ["NIN verified",1714,93,"#3F5F8D"],
                ["KYC documents accepted",1648,90,"#1F7A4D"],
                ["Tier assigned",1612,88,"#F59A2D"],
                ["Active customer",1584,86,"#B7791F"],
              ].map(([n, v, p, c]) => (
                <div key={n}>
                  <div className="flex items-center justify-between text-[12px] mb-1">
                    <span>{n}</span>
                    <span className="tabular font-medium">{v.toLocaleString()} <span className="text-ink-subtle">· {p}%</span></span>
                  </div>
                  <div className="h-1.5 bg-bg-subtle rounded-full"><div className="h-full rounded-full" style={{width: `${p}%`, background: c}} /></div>
                </div>
              ))}
            </div>
          </Card>

          <Card testid="card-kpis">
            <CardHeader title="Operational KPIs · today" subtitle="Live SLAs across the compliance workspace" right={<Activity size={14} className="text-amber-600" />} />
            <div className="p-3 grid grid-cols-2 gap-2">
              {[
                ["Avg alert resolution","2h 14m","-12% wow","#1F7A4D"],
                ["Open cases","85","+4 wow","#B7791F"],
                ["MLRO queue","6","-2 wow","#1F7A4D"],
                ["Transactions screened","42,318","+8% wow","#1F7A4D"],
                ["Sanctions latency p95","84ms","stable","#1F7A4D"],
                ["Models in drift","1","+1 wow","#B7791F"],
                ["NFIU acks pending","6","-1 wow","#1F7A4D"],
                ["BO refreshes due","28","steady","#56544D"],
              ].map(([n, v, t, c]) => (
                <div key={n} className="border border-line rounded-md p-2.5">
                  <div className="text-[10px] text-ink-subtle uppercase tracking-wider">{n}</div>
                  <div className="display text-[18px] font-semibold tabular tracking-tight mt-1">{v}</div>
                  <div className="text-[10px] mt-0.5" style={{color: c}}>{t}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* State drill-down modal */}
      {stateDrill && (
        <div className="fixed inset-0 bg-ink/30 z-50 flex items-center justify-center p-6 backdrop-blur-md animate-fade-in" onClick={() => setStateDrill(null)}>
          <div className="bg-white max-w-[860px] w-full border border-line rounded-[20px] shadow-[0_4px_12px_rgba(11,43,92,0.08),0_32px_64px_rgba(11,43,92,0.22)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-line bg-gradient-to-r from-amber/10 to-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-navy text-white flex items-center justify-center">
                  <Globe2 size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="display text-[16px] font-semibold tracking-tight">{stateDrill} state — alert drill-down</div>
                  <div className="text-[11px] text-ink-subtle">{stateAlerts.summary?.total || 0} alerts · ₦{((stateAlerts.summary?.amount_at_risk || 0)/1000000).toFixed(1)}M at risk · last 30 days</div>
                </div>
              </div>
              <button onClick={() => setStateDrill(null)} className="p-1.5 hover:bg-bg-hover rounded-md" data-testid="state-drill-close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {stateLoading ? (
                <div className="p-12 text-center text-[12px] text-ink-muted">Loading {stateDrill} alerts…</div>
              ) : (
                <>
                  {/* Stat strip */}
                  <div className="grid grid-cols-4 border-b border-line">
                    <div className="p-4 border-r border-line">
                      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">P1 alerts</div>
                      <div className="display text-[22px] font-semibold tabular">{stateAlerts.summary?.p1 || 0}</div>
                    </div>
                    <div className="p-4 border-r border-line">
                      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Open cases</div>
                      <div className="display text-[22px] font-semibold tabular">{stateAlerts.summary?.cases || 0}</div>
                    </div>
                    <div className="p-4 border-r border-line">
                      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">High-risk customers</div>
                      <div className="display text-[22px] font-semibold tabular">{stateAlerts.summary?.high_risk_customers || 0}</div>
                    </div>
                    <div className="p-4">
                      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Branches</div>
                      <div className="display text-[22px] font-semibold tabular">{stateAlerts.summary?.branches || 0}</div>
                    </div>
                  </div>
                  {/* Top typologies */}
                  <div className="p-4 border-b border-line">
                    <div className="text-[11px] uppercase tracking-wider text-ink-subtle mb-2">Top AMLTRIX typologies in {stateDrill}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(stateAlerts.top_typologies || []).map((t, i) => (
                        <span key={i} className="px-2 py-1 text-[10px] bg-bg-warm border border-line rounded-full">
                          {t.typology} <span className="text-ink-subtle">· {t.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* Alert list */}
                  <div>
                    <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-ink-subtle bg-bg-warm">Alerts in {stateDrill}</div>
                    {(stateAlerts.alerts || []).map(a => (
                      <a key={a.id} href={`/alerts/${a.id}`} className="block px-4 py-3 border-b border-line/50 hover:bg-bg-hover transition-colors">
                        <div className="flex items-start gap-3">
                          <PriorityPill priority={a.priority} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-medium truncate">{a.subject_name}</div>
                            <div className="text-[11px] text-ink-subtle truncate">{a.id} · {a.rule} · {a.typology}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-[12px] tabular font-medium">₦{((a.amount_at_risk||0)/1000000).toFixed(1)}M</div>
                            <StatusPill status={a.status} />
                          </div>
                        </div>
                      </a>
                    ))}
                    {(!stateAlerts.alerts || stateAlerts.alerts.length === 0) && (
                      <div className="p-8 text-center text-[12px] text-ink-subtle">No alerts seeded for {stateDrill} in the demo dataset.</div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="px-6 py-3 border-t border-line bg-bg-warm flex items-center justify-between">
              <div className="text-[11px] text-ink-subtle">Data from {stateDrill} branches · last 30 days</div>
              <a href={`/alerts?state=${encodeURIComponent(stateDrill)}`} className="px-3 py-1.5 text-[12px] bg-ink text-white rounded-full hover:bg-navy">View all alerts →</a>
            </div>
          </div>
        </div>
      )}

      {/* Atom AI Executive Summary modal */}
      {aiOpen && (
        <div className="fixed inset-0 bg-ink/30 z-50 flex items-center justify-center p-6 backdrop-blur-md" onClick={() => setAiOpen(false)}>
          <div className="bg-white max-w-[760px] w-full border border-line rounded-[20px] shadow-[0_4px_12px_rgba(11,43,92,0.08),0_32px_64px_rgba(11,43,92,0.18)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-line bg-gradient-to-r from-amber/10 to-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber flex items-center justify-center shadow-[0_4px_10px_rgba(245,154,45,0.4)]">
                  <Sparkles size={16} className="text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="display text-[15px] font-semibold tracking-tight">Atom Nucleus v3.5 — Executive Summary</div>
                  <div className="text-[11px] text-ink-subtle">Atom Foundation Model · Live generation</div>
                </div>
              </div>
              <button onClick={() => setAiOpen(false)} className="p-1.5 hover:bg-bg-hover rounded-md transition-colors">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              {aiLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-10 h-10 border-2 border-amber border-t-transparent rounded-full animate-spin" />
                  <div className="text-[12px] text-ink-muted">Atom Nucleus is drafting your executive summary…</div>
                </div>
              ) : (
                <div className="text-[13px] text-ink leading-relaxed whitespace-pre-wrap">{aiText}</div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-line bg-bg-warm flex items-center justify-between">
              <div className="text-[11px] text-ink-subtle">Audit-grade · Tamper-evident · CBN AI/ML governance compliant</div>
              <button onClick={() => setAiOpen(false)} className="px-4 py-1.5 text-[12px] bg-ink text-white rounded-full hover:bg-navy">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
